import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { compressImage } from '@/lib/imageCompression';

export const useVehiclePhoto = () => {
  const [isUploading, setIsUploading] = useState(false);
  const { toast } = useToast();

  const uploadVehiclePhoto = async (
    file: File,
    userId: string,
    vehicleId: string
  ): Promise<string | null> => {
    setIsUploading(true);

    try {
      // Compress the image first
      const compressedFile = await compressImage(file, {
        maxWidth: 2048,
        maxHeight: 2048,
        quality: 0.8,
        preserveExif: true,
      });

      const fileExt = compressedFile.name.split('.').pop();
      const fileName = `${userId}/${vehicleId}/${Date.now()}.${fileExt}`;

      const { error: uploadError, data } = await supabase.storage
        .from('vehicle-photos')
        .upload(fileName, compressedFile, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from('vehicle-photos')
        .getPublicUrl(fileName);

      return urlData.publicUrl;
    } catch (error) {
      console.error('Error uploading vehicle photo:', error);
      toast({
        title: 'Upload failed',
        description: 'Failed to upload vehicle photo. Please try again.',
        variant: 'destructive',
      });
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  const deleteVehiclePhoto = async (photoUrl: string): Promise<boolean> => {
    try {
      // Extract the file path from the public URL
      const url = new URL(photoUrl);
      const pathParts = url.pathname.split('/vehicle-photos/');
      if (pathParts.length < 2) throw new Error('Invalid photo URL');
      
      const filePath = pathParts[1];

      const { error } = await supabase.storage
        .from('vehicle-photos')
        .remove([filePath]);

      if (error) throw error;

      return true;
    } catch (error) {
      console.error('Error deleting vehicle photo:', error);
      toast({
        title: 'Delete failed',
        description: 'Failed to delete vehicle photo.',
        variant: 'destructive',
      });
      return false;
    }
  };

  return {
    uploadVehiclePhoto,
    deleteVehiclePhoto,
    isUploading,
  };
};
